// @flow
import React, {Component} from 'react';
import {isEqual, pick} from 'lodash';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {FORCE_GRAPHQL_ERROR_HEADER} from '../../queries/searchRequestQuery';
import searchRequestQuery, {
    searchRequestQueryVariables
} from '../../queries/searchRequestQuery';
import {ResultsParams} from '../../../server/graphql/params';
import searchRequestQueryActions from '../../actions/searchRequestQuery';

import type {State as ReduxState} from '../../reducers/index';
import type {SearchParamsType} from '../../reducers/searchParams';
import withQuery from '../withQuery/withQuery';

type DispatchProps = {
    searchRequestQueryComplete: typeof searchRequestQueryActions.searchRequestQueryComplete,
    searchRequestQueryStart: typeof searchRequestQueryActions.searchRequestQueryStart
};

type StateProps = SearchParamsType & {
    abacus: $PropertyType<ReduxState, 'abacus'>,
    pathname: string,
    system: $PropertyType<ReduxState, 'system'>
};

type ConnectorProps = DispatchProps & StateProps;

export const queryConfig = (props: ConnectorProps) => ({
    document: searchRequestQuery,
    options: {
        errorPolicy: 'all',
        context: {
            headers: {
                [FORCE_GRAPHQL_ERROR_HEADER]:
                    typeof window !== 'undefined' && window.forceGraphQLError
            }
        },
        variables: searchRequestQueryVariables(
            props,
            props.abacus,
            props.system
        ),
        ssr: props.system.isGoodBot // we run server side rendering for good bots
    }
});

export function withSearchRequestQuery(WrappedComponent: any) {
    type Props = any;

    return class WithSearchRequestQuery extends Component<Props> {
        componentDidMount() {
            const {data, searchRequestQueryComplete} = this.props;
            if (!data.loading) {
                searchRequestQueryComplete(data);
            }
        }

        shouldComponentUpdate(nextProps: Props) {
            const {loading, results, error} = this.props.data;
            const nextLoading = nextProps.data.loading;
            const nextResults = nextProps.data.results;
            const nextError = nextProps.data.error;

            return (
                loading !== nextLoading ||
                !isEqual(results, nextResults) ||
                !isEqual(error, nextError)
            );
        }

        componentDidUpdate(prevProps: Props) {
            const {data: prevData} = prevProps;
            const {loading: prevLoading} = prevData;
            const {
                data,
                searchRequestQueryComplete,
                searchRequestQueryStart
            } = this.props;
            // we need only check loading states since shouldComponentUpdate
            // guarantees that app has either moved to a loading state
            // or results have changed

            // if !data.loading and !prevLoading, then the cache was used to retrieve data
            // This will guarantee that corresponding dispatches to searchRequestQueryStart
            // always precede those to searchRequestQueryComplete
            if (data.loading) {
                searchRequestQueryStart({data, prevData});
            } else if (prevLoading && !data.loading) {
                searchRequestQueryComplete(data);
            } else {
                searchRequestQueryStart({data, prevData});
                searchRequestQueryComplete(data);
            }
        }

        encodeVariables = () => {
            const {variables} = this.props.data;
            return encodeURIComponent(JSON.stringify(variables));
        };

        render() {
            const graphiqlUrl = `/serp/graphiql?&variables=${this.encodeVariables()}`;
            return (
                <WrappedComponent {...this.props} graphiqlUrl={graphiqlUrl} />
            );
        }
    };
}

export default function connectedWithSearchRequestQuery(
    WrappedComponent: any
): any {
    const mapStateToGraphlProps = ({
        abacus,
        system,
        searchParams
    }: ReduxState): StateProps => ({
        abacus,
        pathname: searchParams.pathname,
        system,
        ...pick(searchParams.params, ResultsParams)
    });

    const mapDispatchToProps = {
        searchRequestQueryStart:
            searchRequestQueryActions.searchRequestQueryStart,
        searchRequestQueryComplete:
            searchRequestQueryActions.searchRequestQueryComplete
    };

    return compose(
        connect(mapStateToGraphlProps, mapDispatchToProps),
        withQuery(queryConfig),
        withSearchRequestQuery
    )(WrappedComponent);
}
